import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { formatDateTime, healthColorMeta, healthNameMeta } from "./statusUtils"

const INSTANCE_ORDER = [
  "Ok",
  "Info",
  "Warning",
  "Degraded",
  "Severe",
  "Pending",
  "Unknown",
  "NoData",
]

function instanceEntries(instances) {
  return Object.entries(instances || {}).sort(
    ([a], [b]) => INSTANCE_ORDER.indexOf(a) - INSTANCE_ORDER.indexOf(b)
  )
}

// Elastic Beanstalk's own view of the environment: colour, state, servers, causes.
export default function HealthSection({ elasticBeanstalk }) {
  const meta = healthColorMeta(elasticBeanstalk?.color)

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${meta.color}`,
        bgcolor: meta.bg,
      }}
    >
      <Column gap={1.5}>
        <Typography
          variant="overline"
          sx={{ color: meta.color, fontWeight: 700 }}
        >
          Elastic Beanstalk health
        </Typography>

        {elasticBeanstalk ? (
          <>
            <Row sx={{ alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Box
                aria-hidden
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  bgcolor: meta.color,
                }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: meta.color }}
              >
                {meta.colorName}
              </Typography>
              {elasticBeanstalk.health_status ? (
                <Typography variant="h6" sx={{ color: meta.color }}>
                  {elasticBeanstalk.health_status}
                </Typography>
              ) : null}
            </Row>

            <Typography variant="body2" color="text.secondary">
              Environment: {elasticBeanstalk.environment_status || "–"}
              {elasticBeanstalk.refreshed_at
                ? ` · updated ${formatDateTime(elasticBeanstalk.refreshed_at)}`
                : ""}
            </Typography>

            <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Servers: {elasticBeanstalk.instance_count}
              </Typography>
              {instanceEntries(elasticBeanstalk.instances).map(
                ([name, count]) => {
                  const chipMeta = healthNameMeta(name)
                  return (
                    <Chip
                      key={name}
                      size="small"
                      label={`${name} ${count}`}
                      sx={{
                        bgcolor: "background.paper",
                        color: chipMeta.color,
                        border: `1px solid ${chipMeta.color}`,
                        fontWeight: 600,
                      }}
                    />
                  )
                }
              )}
            </Row>

            {elasticBeanstalk.causes?.length ? (
              <Column gap={0.5}>
                <Typography variant="subtitle2">Why</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {elasticBeanstalk.causes.map((cause) => (
                    <Typography component="li" variant="body2" key={cause}>
                      {cause}
                    </Typography>
                  ))}
                </Box>
              </Column>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: meta.color }}>
            Health data is unavailable (Grey). Check the AWS permissions and
            that Enhanced health reporting is on.
          </Typography>
        )}
      </Column>
    </Box>
  )
}

HealthSection.propTypes = { elasticBeanstalk: PropTypes.object }
